import { useState } from 'react';
import { useRouter } from './router/useRouter.js';
import { useAuth } from './auth/useAuth.js';
import { LoginView } from './auth/LoginView.js';
import { ResetPasswordView } from './auth/ResetPasswordView.js';
import { AppShell } from './shell/AppShell.js';
import { ToastProvider, UnsavedGuardProvider, useClearToastsOnScreenChange } from './ui/index.js';

/**
 * Root SPA composition (E10-F01-T03 shell + E10-F01-T05 session restore).
 *
 * Owns the SINGLE router instance (`useRouter`) so the session-restore flow and
 * the shell render from the same route state — `useAuth` navigates to the last
 * saved route on a successful restore and the shell reflects it immediately.
 *
 * Auth gate (parity with the legacy `auth.js init()` + `core.js`):
 *   - `restoring`     → a blank splash while the cold-start session check runs
 *     (read persisted token + cached user → confirm with `GET /auth/me`);
 *   - `anonymous`     → the `LoginView` (no token, failed restore, logout, or any
 *     401 that fired `handleUnauthorized`); `main-app` is not rendered;
 *   - `authenticated` → the full `AppShell` with the confirmed principal; clients
 *     + projects were warmed during restore and the last route is reopened.
 */
export function App(): React.JSX.Element {
  const router = useRouter();
  const { status, user, loggingIn, loginError, login, logout, resetRequest } = useAuth({
    navigate: router.navigate,
  });

  // The toast system (E10-F02-T03) wraps the WHOLE app — splash, login, and the
  // shell — so any `useToast()` raised from anywhere lands in the single
  // bottom-right stack the provider renders (parity with the legacy static
  // `#toast-container` that lived outside every view).
  return (
    <ToastProvider>
      <AppRoot
        router={router}
        status={status}
        user={user}
        loggingIn={loggingIn}
        loginError={loginError}
        login={login}
        logout={logout}
        resetRequest={resetRequest}
      />
    </ToastProvider>
  );
}

/** SPA path that opens the password-reset flow for an anonymous visitor. */
const RESET_PATH = '/auth/reset';

function isResetPath(): boolean {
  return typeof window !== 'undefined' && window.location.pathname === RESET_PATH;
}

/**
 * The one-time code carried by an emailed link `/auth/reset?token=<token>`
 * (E12-F08-T13) — read on the first render, before the router rewrites the URL.
 */
function resetTokenFromUrl(): string {
  if (!isResetPath()) return '';
  return new URLSearchParams(window.location.search).get('token')?.trim() ?? '';
}

/** Drop the `/auth/reset` deep link from the address bar when leaving the flow. */
function leaveResetPath(): void {
  if (isResetPath()) window.history.replaceState(null, '', '/');
}

type AppRootProps = Pick<
  ReturnType<typeof useAuth>,
  'status' | 'user' | 'loggingIn' | 'loginError' | 'login' | 'logout' | 'resetRequest'
> & { readonly router: ReturnType<typeof useRouter> };

function AppRoot({
  router,
  status,
  user,
  loggingIn,
  loginError,
  login,
  logout,
  resetRequest,
}: AppRootProps): React.JSX.Element {
  // Anonymous sub-screen (E01-F03): login form or the password-reset flow. A
  // cold load on `/auth/reset` opens the reset flow directly (deep link for the
  // e2e entry point / an emailed link). Read in the state initializer, i.e. on
  // the first render — before the router's mount effect rewrites the URL.
  const [anonScreen, setAnonScreen] = useState<'login' | 'reset'>(() =>
    isResetPath() ? 'reset' : 'login',
  );
  const [loginNotice, setLoginNotice] = useState<string | null>(null);
  // `/auth/reset?token=…` opens the reset view on its second phase with the code.
  const [resetToken, setResetToken] = useState<string>(resetTokenFromUrl);
  const [resetEmail, setResetEmail] = useState('');

  // E12-F08-T13: a 403 password_reset_required dropped the session — open the
  // reset view pre-filled with the user's email, once per event (`seq`). State is
  // adjusted during render (not in an effect) so the login screen never flashes.
  const [seenResetSeq, setSeenResetSeq] = useState(0);
  if (resetRequest && resetRequest.seq !== seenResetSeq) {
    setSeenResetSeq(resetRequest.seq);
    setResetEmail(resetRequest.email ?? '');
    setLoginNotice(null);
    setAnonScreen('reset');
  }

  // E06-F01-T15 — toasts from the previous screen do not follow the user to the
  // next one (a red error toast used to survive every navigation).
  useClearToastsOnScreenChange(
    `${status}:${anonScreen}:${router.route.view}:${router.route.id ?? ''}`,
  );

  if (status === 'restoring') {
    // Brief cold-start splash; the optimistic cached user (if any) renders the
    // shell on the next tick once restore resolves.
    return (
      <div
        data-testid="auth-splash"
        className="flex min-h-screen items-center justify-center bg-slate-900"
      >
        <span className="brand-font text-2xl font-bold tracking-wide text-[#D4AF37]">
          EER GLOBAL
        </span>
      </div>
    );
  }

  if (status === 'anonymous') {
    if (anonScreen === 'reset') {
      return (
        <ResetPasswordView
          // Remount on every reset-gate event so the new email pre-fill applies.
          key={`reset-${seenResetSeq}`}
          initialEmail={resetEmail}
          initialToken={resetToken}
          onResetComplete={() => {
            leaveResetPath();
            setResetToken('');
            setLoginNotice('Password updated. Please log in with your new password.');
            setAnonScreen('login');
          }}
          onBackToLogin={() => {
            leaveResetPath();
            setResetToken('');
            setAnonScreen('login');
          }}
        />
      );
    }
    return (
      <LoginView
        onLogin={login}
        loggingIn={loggingIn}
        error={loginError}
        notice={loginNotice}
        onForgotPassword={() => {
          setLoginNotice(null);
          setAnonScreen('reset');
        }}
      />
    );
  }

  return (
    <div id="main-app" data-testid="main-app">
      {/* The unsaved-changes guard wraps the shell so every navigation routed
          through `useUnsavedGuard().guard(...)` is gated by the active editor's
          dirty state (E10-F02-T02). With no editor registered the guard is inert
          and navigation proceeds directly. */}
      <UnsavedGuardProvider>
        <AppShell user={user} router={router} onLogout={() => void logout()} />
      </UnsavedGuardProvider>
    </div>
  );
}
